import { Group, KeyboardArrowDown } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Popover,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { FC, useState } from "react";

import { useSelectedGroups } from "../features/auth/hooks/useSelectedGroups";

interface GroupsDisplayProps {
  showLabel?: boolean;
  variant?: "outlined" | "filled";
  size?: "small" | "medium";
}

/**
 * Component to display the current analysis groups
 * Shows which groups the user has access to and allows selection when multiple groups are available
 */
const GroupsDisplay: FC<GroupsDisplayProps> = ({
  showLabel = true,
  variant = "outlined",
  size = "small"
}) => {
  const {
    selectedGroups,
    availableGroups,
    updateSelectedGroups,
    isFiltered,
    hasMultipleGroups
  } = useSelectedGroups();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tempSelectedGroups, setTempSelectedGroups] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (hasMultipleGroups) {
      setAnchorEl(event.currentTarget);
      setTempSelectedGroups(selectedGroups);
      setError(null);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setTempSelectedGroups([]);
    setError(null);
  };

  const handleGroupToggle = (groupName: string) => {
    const newSelection = tempSelectedGroups.includes(groupName)
      ? tempSelectedGroups.filter(g => g !== groupName)
      : [...tempSelectedGroups, groupName];

    setTempSelectedGroups(newSelection);

    if (newSelection.length === 0) {
      setError("At least one group must be selected");
    } else {
      setError(null);
    }
  };

  const handleSave = () => {
    if (tempSelectedGroups.length === 0) {
      setError("At least one group must be selected");
      return;
    }

    try {
      updateSelectedGroups(tempSelectedGroups);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const open = Boolean(anchorEl);

  // Determine display text
  const getDisplayText = () => {
    if (!selectedGroups || selectedGroups.length === 0) {
      return "Groups: ALL";
    }

    const groupsText = selectedGroups.join(", ");
    const fullText = `Groups: ${groupsText}`;

    // Truncate if too long (>50 characters)
    if (fullText.length > 50) {
      const truncatedGroups = groupsText.substring(0, 50 - "Groups: ".length - "...".length);
      return `Groups: ${truncatedGroups}...`;
    }

    return fullText;
  };

  // Generate tooltip content
  const getTooltipContent = () => {
    if (!hasMultipleGroups) {
      return (
        <Box>
          <Typography variant="body2">Analysis Groups: ALL</Typography>
          <Typography variant="caption">You have access to all analysis groups</Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Typography variant="body2">Selected Groups</Typography>
        <Typography variant="caption">
          {hasMultipleGroups ? "Click to change selection" : "You have access to the following groups:"}
        </Typography>
        {selectedGroups.map((groupName, index) => (
          <Typography key={index} variant="caption" display="block">
            • {groupName}
          </Typography>
        ))}
      </Box>
    );
  };

  const displayText = getDisplayText();
  const labelText = showLabel ? displayText : (hasMultipleGroups ? selectedGroups.join(", ") : "ALL");

  return (
    <>
      <Tooltip title={getTooltipContent()}>
        <Chip
          icon={<Group fontSize="small" />}
          label={labelText}
          size={size}
          variant={variant}
          onClick={handleClick}
          deleteIcon={hasMultipleGroups ? <KeyboardArrowDown fontSize="small" /> : undefined}
          onDelete={hasMultipleGroups ? handleClick : undefined}
          sx={{
            p: 1,
            cursor: hasMultipleGroups ? "pointer" : "default",
            backgroundColor: isFiltered ? "primary.light" : undefined,
            color: isFiltered ? "primary.contrastText" : undefined,
            "&:hover": hasMultipleGroups ? {
              backgroundColor: isFiltered ? "primary.main" : "action.hover"
            } : {}
          }}
        />
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="h6" gutterBottom>
            Select Analysis Groups
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormGroup>
            {availableGroups.map((groupName) => (
              <FormControlLabel
                key={groupName}
                control={
                  <Checkbox
                    checked={tempSelectedGroups.includes(groupName)}
                    onChange={() => handleGroupToggle(groupName)}
                    size="small"
                  />
                }
                label={groupName}
              />
            ))}
          </FormGroup>

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={tempSelectedGroups.length === 0}
            >
              OK
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default GroupsDisplay;
